import React, { useMemo, useState, useEffect, useContext } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { DataGrid, KeenIcon } from '@/components';
import { Loading } from '@/layouts/buddy/loading';
import { UserDetailsData } from '@/pages/buddy/types/UserDetailsData';
import { enqueueSnackbar } from 'notistack';
import {AuthContext} from "@/auth/providers/JWTProvider.tsx";


interface UsersProps {
    users: UserDetailsData[];
    loading: boolean;
}

const Users: React.FC<UsersProps> = ({ users, loading }) => {
    const storageFilterId = 'users-filter';
    const [searchTerm, setSearchTerm] = useState(() => {
        return localStorage.getItem(storageFilterId) || '';
    });

    useEffect(() => {
        localStorage.setItem(storageFilterId, searchTerm);
    }, [searchTerm]);

    const authContext = useContext(AuthContext);

    const handleImpersonate = async (p_id: string) => {
        if (!authContext?.impersonate) {
            enqueueSnackbar('Impersonation function is not available.', {
                variant: 'solid',
                state: 'danger',
            });
            return;
        }

        try {
            await authContext.impersonate(p_id);
        } catch (error) {
            enqueueSnackbar('Failed to impersonate user.', {
                variant: 'solid',
                state: 'danger',
            });
            console.error('Impersonation error:', error);
        }
    };

    const filteredData = useMemo(() => {
        return users.filter((user) => {
            const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
            return (
                fullName.includes(searchTerm.toLowerCase()) ||
                user.email?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        });
    }, [users, searchTerm]);

    const columns = useMemo<ColumnDef<UserDetailsData>[]>(() => [
        {
            accessorKey: 'full_name',
            header: 'Name',
            enableSorting: true,
            cell: ({ row }) => (
                <div className="flex flex-col">
                    <span className="font-medium">{row.original.first_name} {row.original.last_name}</span>
                </div>
            ),
        },
        { accessorKey: 'email', header: 'Email' , enableSorting: true },
        { accessorKey: 'company', header: 'Company', enableSorting: true  },
        { accessorKey: 'country', header: 'Country', enableSorting: true  },
        { accessorKey: 'phone', header: 'Phone', enableSorting: true  },
        { accessorKey: 'occupation', header: 'Occupation', enableSorting: true  },
        {
            accessorKey: 'roles',
            header: 'Roles',
            enableSorting: true,
            cell: ({ getValue }) => (getValue() as string[]).join(', ') || '—',
        },
        {
            accessorKey: 'permissions',
            header: 'Permissions',
            enableSorting: true,
            cell: ({ getValue }) => (getValue() as string[]).join(', ') || '—',
        },
        {
            accessorKey: 'is_active',
            header: 'Active',
            enableSorting: true,
            cell: ({ getValue }) => (getValue() ? 'Yes' : 'No'),
        },
        {
            accessorKey: 'is_verified',
            header: 'Verified',
            enableSorting: true,
            cell: ({ getValue }) => (getValue() ? 'Yes' : 'No'),
        },
        {
            id: 'impersonate',
            header: 'Login as',
            cell: ({ row }) => (
                <button
                    className="btn btn-sm btn-icon btn-clear btn-light"
                    onClick={() => handleImpersonate(row.original.p_id)}
                >
                    <KeenIcon icon="lock" />
                </button>
            ),
            meta: {
                className: 'w-[120px]',
            },
        },
    ], [handleImpersonate]);

    if (loading) return <Loading loading={loading} />;

    return (
        <div className="card card-grid min-w-full">
            <div className="card-header flex-wrap py-5">
                <h3 className="card-title">Users</h3>
                <div className="relative">
                    <KeenIcon
                        icon="magnifier"
                        className="text-md text-gray-500 absolute top-1/2 left-0 transform -translate-y-1/2 ml-3"
                    />
                    <input
                        type="text"
                        placeholder="Search users"
                        className="input input-sm pl-8"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>
            <div className="card-body">
                <DataGrid
                    key={searchTerm}
                    columns={columns}
                    data={filteredData}
                    pagination={{ size: 10 }}
                />
            </div>
        </div>
    );
};

export { Users };
